import { oktaClient as api, partnerClient } from '@modules/core/services';

import { AddUserPayload, AddUserRequestPayload, UserStatuses } from '@modules/teams/types';
import { User } from '@modules/core/types';
import { isNonAdminRole } from '../utils';
import { isUserAdministrator } from '@modules/auth/utils/userRole';
import { UserToUpdate } from '../types/UpdateTeamMember';

const makeSearchStatusByUser = (user: User): string => {
  const statuses: UserStatuses[] = [
    UserStatuses.ACTIVE,
    UserStatuses.PROVISIONED,
    UserStatuses.LOCKED_OUT,
    UserStatuses.RECOVERY,
    UserStatuses.PASSWORD_EXPIRED,
  ];
  if (isUserAdministrator(user)) {
    statuses.push(UserStatuses.DEPROVISIONED);
  }

  let search = '(';

  statuses.forEach((status: UserStatuses, index) => {
    search = search.concat(`status eq "${status as string}"`);
    if (statuses.length - 1 !== index) {
      search = search.concat(' or ');
    }
  });

  search = search.concat(')');
  return search;
};

export const fetchTeams = async (user: User): Promise<User[] | undefined> => {
  return await partnerClient.get('users', {
    params: {
      search: makeSearchStatusByUser(user),
    },
  });
};

export const addUser = async (addTeamMemberPayload: AddUserPayload): Promise<any> => {
  const { firstName, lastName, email, roleId, groupId } = addTeamMemberPayload;

  const payload: AddUserRequestPayload = {
    profile: {
      firstName,
      lastName,
      email,
      login: email,
    },
    groupIds: [],
  };

  if (groupId !== undefined) {
    payload.groupIds = [groupId];
  }

  // only set pp_role if user is non-administrator
  if (isNonAdminRole(roleId)) {
    payload.profile.pp_role = roleId;
  }

  return await api.post('users', payload);
};

export const changeMemberRole = async (roleId: string | number, userId: string): Promise<any> => {
  const payload = {
    profile: {
      pp_role: roleId,
    },
  };
  return await api.post(`users/${userId}`, payload);
};

export const deleteUser = async (userId: string): Promise<any> => {
  return await api.delete(`users/${userId}`);
};

export const resetUserPassword = async (userId: string): Promise<any> => {
  return await api.post(`users/${userId}/lifecycle/reset_password`);
};

export const deactivateUser = async (userId: string): Promise<void> => {
  return await api.post(`users/${userId}/lifecycle/deactivate`);
};

export const activateUser = async (userId: string): Promise<void> => {
  return await api.post(`users/${userId}/lifecycle/activate`);
};

export const resetMFA = async (userId: string): Promise<void> => {
  return await api.post(`users/${userId}/lifecycle/reset_factors`);
};

export const updateUser = async (userId: string, user: UserToUpdate): Promise<void> => {
  const profile = {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
  };

  return await api.post(`users/${userId}`, { profile });
};
